/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import _ from 'underscore' // Npm: underscore.js library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Checkbox,
  Flex,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import BlockDestinationAddressUpsert from 'components/BlockDestinationAddressUpsert'
import BlockDestinationAddressDelete from 'components/BlockDestinationAddressDelete'
import BlockDestinationAddressDropDownOptions from 'components/BlockDestinationAddressDropDownOptions'


/*
 * GRAPHS
 */
import BlockDestinationAddressReadQuery from './__query__/index.blockDestinationAddress.read.query'


/*
 * STYLES
 */
import { buttonStyle, tabStyles } from './index.style'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _Customers = Object.React.App.enums.CONTENT_TRANSLATION_TYPE.enums.map(i => i.key)
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ 'items': [] })
  const [selectAll, setSelectAll] = React.useState(false)
  const [tabIndex, setTabIndex] = React.useState(0)
  const [selectedIds, setSelectedIds] = React.useState([])

  const _QueryBlockDestinationAddressRead = useQuery(
    BlockDestinationAddressReadQuery,
    {
      'variables': {
        'type': _Customers[tabIndex],
        'skip': paginationModel.page * paginationModel.pageSize,
        'take': paginationModel.pageSize
      },
      'fetchPolicy': Object.React.App.fetchPolicy,
      'pollInterval': Object.React.App.pollInterval
    }
  )

  const _isFirstLoadCompleted = React.useRef(false)
  const { 'onOpen': onBlockDestinationAddressOpen, 'isOpen': isBlockDestinationAddressOpen, 'onClose': onBlockDestinationAddressClose } = useDisclosure()
  const { 'isOpen': isBlockDestinationAddressUpdateOpen, 'onOpen': onBlockDestinationAddressUpdateOpen, 'onClose': onBlockDestinationAddressUpdateClose } = useDisclosure()
  const { 'isOpen': isBlockDestinationAddressDeleteOpen, 'onOpen': onBlockDestinationAddressDeleteOpen, 'onClose': onBlockDestinationAddressDeleteClose } = useDisclosure()
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_QueryBlockDestinationAddressRead.data) {
      // Update states.
      setSelectedIds([])
      setSelectAll(false)
    }
  }, [
    isBlockDestinationAddressUpdateOpen,
    isBlockDestinationAddressOpen,
    isBlockDestinationAddressDeleteOpen,
    tabIndex,
    paginationModel.page
  ])

  // Event handler.
  const _HandleSelectAll = () => {
    // Check if selectAll is true.
    setSelectAll(!selectAll)

    // Check if selectAll is true.
    setSelectedIds(
      selectAll ? [] : _QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead?.map(
        item => item.id
      ) || []
    )
  }

  const _HandleCheckboxChange = id => {
    // Check if id is already in selectedIds.
    if (selectedIds.includes(id)) {
      // Remove id from selectedIds.
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
    } else {
      // Add id to selectedIds.
      setSelectedIds([...selectedIds, id])
    }
  }

  // Data assignment.
  if (!_QueryBlockDestinationAddressRead.loading && 0 < _QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead?.length || !_.every(_.pluck(_QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead, 'status'), j => _successFlags.includes(j))) _isFirstLoadCompleted.current = false

  // Define columns
  const columns = React.useMemo(() => [
    {
      'field': 'checkbox',
      'headerName': '',
      'align': 'center',
      'headerAlign': 'center',
      'width': 60,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true,
      renderHeader: () => (
        <Checkbox bg='gray.100' onChange={_HandleSelectAll} isChecked={selectAll} />
      ),
      renderCell: ({ row }) => (
        <Checkbox
          bg='gray.100'
          isChecked={selectedIds.includes(row.id)}
          onChange={() => _HandleCheckboxChange(row.id)}
        />
      )
    },
    {
      'field': 'sno',
      'headerName': 'S No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true,
      'headerAlign': 'center'
    },
    {
      'field': 'customerName',
      'headerName': 'Customer Name',
      'description': 'Name of the customer.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'disableColumnMenu': true
    },
    {
      'field': 'destinationAddress',
      'headerName': 'Destination Address',
      'description': 'Blocked destination addresses.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 180,
      'disableColumnMenu': true
    },
    {
      'field': 'senderIds',
      'headerName': "Sender Id's",
      'description': 'Associated sender IDs.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'disableColumnMenu': true
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Creation date.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last update date.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Available actions.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      renderCell: ({ row }) => (
        <BlockDestinationAddressDropDownOptions
          blockDestinationAddressId={row?.id}
          onBlockDestinationAddressUpdate={onBlockDestinationAddressUpdateOpen}
          onBlockDestinationAddressDelete={onBlockDestinationAddressDeleteOpen}
          selectedIds={selectedIds}
          blockDestinationAddressIds={[...selectedIds, row?.id]}
        />
      )
    }
  ], [selectAll, selectedIds])

  // Define rows
  const rows = React.useMemo(() => _.compact(_QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead?.map((item, index) => _successFlags.includes(item.status) ? ({
    ...item,
    'id': item?.id || `block-${index}-${Date.now()}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'customerName': item?.['TERMINATION' === _Customers[tabIndex] ? 'VendorAccount' : 'CustomerAccount']?.displayName ?? '-',
    'destinationAddress': _.isEmpty(item?.omitDestinationAddress) ? '-' : item?.omitDestinationAddress?.join(', '),
    'senderIds': _.isEmpty(item?.SenderId) ? '-' : _.compact(_.flatten(_.pluck(item?.SenderId, 'senderId')))?.join(', '),
    'createdAt': _.isEmpty(item.createdAt) ? '-' : Moment(item.createdAt).format('YYYY-MM-DD'),
    'updatedAt': _.isEmpty(item?.updatedAt) ? '-' : Moment(item?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead, paginationModel.page, paginationModel.pageSize, _Customers, tabIndex])

  // Return component.
  return (
    <>
      <Flex className='blockDestinationAddress base' height='unset !important'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          height='100%'
        >
          <Flex
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            bg='linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)'
            borderTopLeftRadius='20px'
            borderTopRadius='20px'
            p={_isCurrentViewMobile ? '12px' : '22px'}>
            <Tooltip label='Block new Number.' fontSize='sm'>
              <Button
                w='auto'
                onClick={onBlockDestinationAddressOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}>
                Block Number
              </Button>
            </Tooltip>
          </Flex>
          <Tabs
            variant='enclosed'
            onChange={r => setTabIndex(r)}
            display='flex'
            flexDirection='column'
            align='center'
            w='100%'
            h='90%'
            bg='white'
            isFitted
          >
            <TabList bg='linear-Gradient(73deg,#390EF6 0%, rgba(146, 68, 244, 1) 100%)' overflowX={_isCurrentViewMobile ? 'auto' : 'none'}>
              {_Customers.map(u => (
                <Tab
                  color='white'
                  textDecoration={'line-through'}
                  _focus={{ 'boxShadow': 'none' }}
                  _selected={{ 'textDecoration': 'none' }}
                  style={{ ...tabStyles, 'border': 'none', 'borderRadius': 0 }}
                  p='1rem'
                  key={String.random(8)}
                >
                  {u}
                </Tab>
              ))}
            </TabList>
            <Flex
              height='590px'
              flexDirection='column'
              border='2px solid #3A0FF6'
              borderTop='none'
              borderBottomRadius='15px'>
              {/* MUI DataGrid */}
              <DataGrid
                key={`${paginationModel.page}-${paginationModel.pageSize}-${tabIndex}`}
                rows={rows}
                columns={columns}
                rowHeight={58}
                rowCount={_.pluck(_QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead, '_totalCount')?.[0] || 0}
                paginationMode='server'
                filterMode='client'
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                pageSizeOptions={[8, 30, 24, 100]}
                slots={{
                  'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                  'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                  'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
                }}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
                loading={_QueryBlockDestinationAddressRead?.loading}
                disableColumnMenu={false}
                sx={{
                  '& .MuiDataGrid-main': {
                    border: 'none',
                    borderRadius: 0
                  }
                }}
              />
            </Flex>
          </Tabs>
        </Flex>
        <Modal
          size='md'
          title='BD Address Create'
          isOpen={isBlockDestinationAddressOpen}
          onClose={onBlockDestinationAddressClose}
          isCentered={false}
        >
          <BlockDestinationAddressUpsert
            isCreateOnly={true}
            type={_Customers[tabIndex]}
            isVendor={'TERMINATION' === _Customers[tabIndex]}
          />
        </Modal>
        <Modal
          size='md'
          title='BD Address Update'
          isOpen={isBlockDestinationAddressUpdateOpen}
          onClose={onBlockDestinationAddressUpdateClose}
          isCentered={false}
        >
          <BlockDestinationAddressUpsert
            isCreateOnly={false}
            type={_Customers[tabIndex]}
            isVendor={'TERMINATION' === _Customers[tabIndex]}
          />
        </Modal>
        <Modal
          size='md'
          title='BD Address Delete'
          isOpen={isBlockDestinationAddressDeleteOpen}
          onClose={onBlockDestinationAddressDeleteClose}
        >
          <BlockDestinationAddressDelete />
        </Modal>
      </Flex>
    </>
  )
}

/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORT
 */
export default Index
